import { useEffect, useRef, useState } from "react";

/**
 * FastReveal — reveal ligero puramente CSS (no framer-motion) que respeta
 * la variable global `--reveal-dur`. Cuando el usuario scrollea rápido y
 * `<html data-velocity="fast">` está activo, --reveal-dur baja a 240ms.
 *
 * Ideal para contenido secundario donde queremos que el usuario que
 * escanea rápido no espere: reviews, bullets, cards, tiles.
 */
export function FastReveal({
  children,
  delay = 0,
  y = 14,
  once = true,
  className = "",
  as: Tag = "div",
  ...rest
}) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return;
    }
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          if (once) io.disconnect();
        } else if (!once) {
          setVisible(false);
        }
      },
      { rootMargin: "0px 0px -8% 0px", threshold: 0.16 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [once]);

  const dur = "var(--reveal-dur, 700ms)";
  const easing = "cubic-bezier(0.22, 1, 0.36, 1)";
  return (
    <Tag
      ref={ref}
      className={`velocity-reveal ${className}`}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? "translate3d(0,0,0)" : `translate3d(0,${y}px,0)`,
        transition: `opacity ${dur} ${easing} ${delay}s, transform ${dur} ${easing} ${delay}s`,
        willChange: visible ? "auto" : "opacity, transform",
      }}
      {...rest}
    >
      {children}
    </Tag>
  );
}

/**
 * FastStagger — orquesta un stagger de sus hijos usando --reveal-dur.
 * Cada hijo se renderiza dentro de un FastReveal con delay incremental.
 */
export function FastStagger({ children, step = 0.06, delay = 0, y = 14, className = "" }) {
  const items = Array.isArray(children) ? children : [children];
  return (
    <div className={className}>
      {items.map((child, i) => (
        <FastReveal key={i} delay={delay + i * step} y={y}>
          {child}
        </FastReveal>
      ))}
    </div>
  );
}

export default FastReveal;
